"use client";
import React from "react";
import { useTranslation } from "react-i18next";

// Los mismos contactos que usa el resto del login.
const SUPPORT_EMAIL = "contacto@briancallimusic.com";
const WHATSAPP_NUMBER = "5491173728515";

/**
 * Cartel que reemplaza toda la pantalla de login cuando la cuenta está cerrada.
 *
 * Los datos vienen del 403 del backend (`revoked` en el body de /api/auth/send-code):
 * mail, número de caso, fecha, MOTIVO, y la nota interna sólo si al revocar se marcó
 * que se muestre.
 *
 * ---------------------------------------------------------------------------
 * Por qué hay dos versiones
 * ---------------------------------------------------------------------------
 *
 * Hasta el 25-ago-2026 había una sola, escrita para el que sube los libros a un sitio
 * de descarga: "no corresponde reembolso", "de forma permanente", "reclamar por la vía
 * legal". Cuando se empezó a cerrar cuentas por parecer compartidas, esa misma pantalla
 * le acusaba de piratería a alguien que capaz le prestó la cuenta al hermano, y no le
 * daba ninguna forma de contestar.
 *
 *   - DIFUSIÓN (download_site, resale, messaging_group): el texto duro de siempre.
 *   - CUENTA COMPARTIDA (account_sharing, other): el acceso está PAUSADO, se explica
 *     qué se vio, y se pide que escriba. Sin acusar de robo.
 *
 * Las dos terminan con WhatsApp y mail, porque de los dos lados puede haber un error
 * nuestro y quien está del otro lado pagó.
 */

/** Motivos que van con el texto duro. Cualquier otro cae en la versión suave. */
const MOTIVOS_DIFUSION = ["download_site", "resale", "messaging_group"];

export default function RevokedNotice({ email, caseCode, revokedAt, note, reason }) {
  const { t, i18n } = useTranslation();

  const lang = (i18n.language || "es").split("-")[0];
  const esDifusion = MOTIVOS_DIFUSION.includes(reason);

  // El acento: rojo para difusión, ámbar para el resto. La cuenta pausada no se
  // anuncia con el mismo color que una cancelación definitiva.
  const acento = esDifusion ? "#8c1c22" : "#8a5a00";
  const fondoLista = esDifusion ? "#faf5f5" : "#fbf7ef";

  const textos = esDifusion
    ? {
        badge: "revoked_badge",
        title: "revoked_title",
        parrafos: ["revoked_p1", "revoked_p2", "revoked_p3"],
        listTitle: "revoked_list_title",
        items: ["revoked_li1", "revoked_li2", "revoked_li3", "revoked_li4"],
        voice: "revoked_voice",
      }
    : {
        badge: "revoked_share_badge",
        title: "revoked_share_title",
        // Dos párrafos, no tres: el texto elegido dice lo que tiene que decir en dos.
        parrafos: ["revoked_share_p1", "revoked_share_p2"],
        listTitle: "revoked_share_list_title",
        items: ["revoked_share_li1", "revoked_share_li2", "revoked_share_li3"],
        // Sin cita de cierre a propósito: acá el texto no necesita rematar con nada.
        voice: null,
      };

  let fecha = "";
  if (revokedAt) {
    const d = new Date(revokedAt);
    if (!Number.isNaN(d.getTime())) {
      fecha = new Intl.DateTimeFormat(lang, {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(d);
    }
  }

  const meta = [
    { label: t("revoked_label_account"), value: email },
    caseCode ? { label: t("revoked_label_case"), value: `#${caseCode}` } : null,
    fecha ? { label: t("revoked_label_date"), value: fecha } : null,
  ].filter(Boolean);

  // El caso viaja en el mensaje para no tener que pedírselo después.
  const waHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    t("revoked_help_wa_msg", { email: email || "", case: caseCode || "" }),
  )}`;
  const mailHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    t("revoked_help_mail_subject", { case: caseCode || "" }),
  )}`;

  return (
    <div className="container position-relative pt-130 pb-60">
      <main className="main-rows">
        <div className="row mainRow" style={{ background: "#fff" }}>
          <div className="col-sm-12 py-5">
            <div style={{ maxWidth: 720, margin: "0 auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-block",
                    width: 34,
                    height: 34,
                    border: `2px solid ${acento}`,
                    borderRadius: "50%",
                    position: "relative",
                    flex: "none",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      width: 22,
                      height: 2,
                      background: acento,
                      transform: "translate(-50%, -50%) rotate(-45deg)",
                    }}
                  />
                </span>
                <strong
                  style={{
                    fontSize: 12,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: acento,
                  }}
                >
                  {t(textos.badge)}
                </strong>
              </div>

              <h1
                style={{
                  fontSize: "clamp(1.6rem, 4vw, 2.25rem)",
                  lineHeight: 1.15,
                  color: acento,
                  fontWeight: 600,
                  marginBottom: 20,
                }}
              >
                {t(textos.title)}
              </h1>

              <div
                style={{
                  borderTop: "1px solid #ececec",
                  borderBottom: "1px solid #ececec",
                  padding: "10px 0",
                  marginBottom: 22,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px 18px",
                  fontSize: 13,
                  color: "#7a7a7a",
                }}
              >
                {meta.map((m) => (
                  <div key={m.label} style={{ overflowWrap: "anywhere" }}>
                    {m.label}: <strong style={{ color: "#14171d" }}>{m.value}</strong>
                  </div>
                ))}
              </div>

              {textos.parrafos.map((key) => (
                <p key={key} style={{ color: "#23262d", marginBottom: 16 }}>
                  {t(key)}
                </p>
              ))}

              <p
                style={{
                  fontSize: 12,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: acento,
                  fontWeight: 700,
                  marginBottom: 8,
                  marginTop: 24,
                }}
              >
                {t(textos.listTitle)}
              </p>
              <ul
                style={{
                  listStyle: "none",
                  padding: "18px 20px",
                  margin: "0 0 24px",
                  background: fondoLista,
                  borderLeft: `3px solid ${acento}`,
                }}
              >
                {textos.items.map((key) => (
                  <li
                    key={key}
                    style={{
                      display: "flex",
                      gap: 10,
                      color: "#23262d",
                      marginBottom: 10,
                      lineHeight: 1.55,
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        flex: "none",
                        width: 6,
                        height: 6,
                        marginTop: 8,
                        background: acento,
                        transform: "rotate(45deg)",
                      }}
                    />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>

              {/* Sólo si al revocar se marcó "mostrar la nota en el cartel". */}
              {note && (
                <div
                  style={{
                    border: "1px solid #ececec",
                    padding: "14px 16px",
                    marginBottom: 24,
                    fontSize: 14,
                    color: "#23262d",
                  }}
                >
                  <strong style={{ display: "block", marginBottom: 4 }}>
                    {t(esDifusion ? "revoked_where" : "revoked_share_where")}
                  </strong>
                  <span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{note}</span>
                </div>
              )}

              {textos.voice && (
                <p
                  style={{
                    borderLeft: "2px solid #d8d8d8",
                    paddingLeft: 18,
                    color: "#14171d",
                    marginBottom: 28,
                    lineHeight: 1.62,
                  }}
                >
                  {t(textos.voice)}
                </p>
              )}

              {/* Contacto. Va en las dos versiones: de los dos lados puede haber un
                  error nuestro, y del otro lado hay alguien que pagó. */}
              <div style={{ borderTop: "1px solid #ececec", paddingTop: 22 }}>
                <p
                  style={{
                    fontSize: 12,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: acento,
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  {t("revoked_help_title")}
                </p>
                <p style={{ color: "#23262d", marginBottom: 18 }}>{t("revoked_help_intro")}</p>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-mod btn-circle btn-small"
                    style={{ background: "#25D366", borderColor: "#25D366", color: "#fff" }}
                  >
                    <i className="fa-whatsapp me-2" aria-hidden="true" />
                    {t("revoked_help_wa")}
                  </a>
                  <a
                    href={mailHref}
                    className="btn btn-mod btn-circle btn-small btn-border"
                    style={{ borderColor: "#c9c9c9", color: "#23262d" }}
                  >
                    {t("revoked_help_mail")}
                  </a>
                </div>

                <p style={{ fontSize: 13, color: "#6b6b6b", marginBottom: 0 }}>
                  {t("revoked_help_foot", { email: SUPPORT_EMAIL, case: caseCode || "" })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
